"use client";

import { useEffect, useState } from "react";
import { Mic, ShieldAlert, Settings, LockKeyhole, RefreshCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./dialog";
import { Button } from "./button";

const StepIcon = ({ icon: Icon, label }) => (
  <Icon
    className="inline-block h-3.5 w-3.5 shrink-0 align-[-2px] text-amber-800"
    aria-label={label}
  />
);

export default function MicPermissionModal({
  isOpen,
  isBlocked = false,
  isRequesting = false,
  onEnable,
  onClose,
  autoRequest = true,
}) {
  const [hasAutoRequested, setHasAutoRequested] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setHasAutoRequested(false);
      return;
    }
    if (!isBlocked && autoRequest && !hasAutoRequested && onEnable) {
      setHasAutoRequested(true);
      onEnable();
    }
  }, [isOpen, isBlocked, autoRequest, hasAutoRequested, onEnable]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose?.();
      }}
    >
      <DialogContent className="w-[calc(100%-1.5rem)] max-h-[min(90vh,640px)] overflow-y-auto p-4 sm:max-w-md sm:p-6">
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
            {isBlocked ? (
              <ShieldAlert className="h-8 w-8 text-amber-600" />
            ) : (
              <Mic className="h-8 w-8 text-blue-600" />
            )}
          </div>
          <DialogTitle className="text-xl font-bold text-gray-900">
            {isBlocked ? "Microphone access is blocked" : "Microphone access required"}
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            {isBlocked
              ? "This site cannot use your microphone until you allow it in your browser settings."
              : "VirtualScribe needs microphone access to transcribe and record your visit. Your browser will ask you to allow it."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!isBlocked ? (
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              {isRequesting
                ? "Waiting for your browser permission prompt…"
                : 'Click "Allow microphone" below, then choose Allow in the browser dialog.'}
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900 space-y-3 sm:px-4">
              <p className="font-medium flex items-start gap-2 leading-snug sm:items-center">
                <Settings className="mt-0.5 h-4 w-4 shrink-0 sm:mt-0" />
                <span>How to enable microphone</span>
              </p>
              <ol className="space-y-2.5">
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-200/80 text-[11px] font-semibold">
                    1
                  </span>
                  <p className="min-w-0 leading-relaxed">
                    Click the lock icon{" "}
                    <StepIcon icon={LockKeyhole} label="lock" /> just before
                    the website address at the top of your browser.
                  </p>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-200/80 text-[11px] font-semibold">
                    2
                  </span>
                  <p className="min-w-0 leading-relaxed">
                    Find Microphone <StepIcon icon={Mic} label="microphone" />{" "}
                    and change it to Allow.
                  </p>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-200/80 text-[11px] font-semibold">
                    3
                  </span>
                  <p className="min-w-0 leading-relaxed">
                    Refresh <StepIcon icon={RefreshCcw} label="refresh" /> the
                    page and start recording again.
                  </p>
                </li>
              </ol>
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isRequesting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            {!isBlocked && (
              <Button
                type="button"
                onClick={onEnable}
                disabled={isRequesting}
                className="w-full bg-blue-600 hover:bg-blue-700 sm:w-auto"
              >
                <Mic className="mr-2 h-4 w-4" />
                {isRequesting ? "Requesting access…" : "Allow microphone"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
